import { ChartBar, Cube, CurrencyDollar, Ticket } from '@ads/icons';
import { eventDashboardQueries } from '@linka/http/event-dashboard';
import { useTranslation } from '@linka/i18n';
import { cn } from '@linka/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Suspense, useState } from 'react';
import { useParams } from 'react-router';
import { Skeleton } from '../../skeleton/skeleton';
import { CheckInChart } from './chart/check-in';
import { CouponsChart } from './chart/coupons';
import { ProductsAvailability } from './chart/products-availability';
import { ProductsByType } from './chart/products-by-type';
import { ProductsStockLevel } from './chart/products-stock-level';
import { ProductsTopRevenue } from './chart/products-top-revenue';
import { ProductsWithdrawn } from './chart/products-withdrawn';
import { SalesByPaymentMethod } from './chart/sales-by-payment-method';
import { SalesByStatusChart } from './chart/sales-by-status';
import { SalesByTicketsChart } from './chart/sales-by-ticket';
import { SalesByTypeChart } from './chart/sales-by-type';
import { SalesCountAndTotalByDayChart } from './chart/sales-count-and-total-by-day';
import { SalesCountByDayChart } from './chart/sales-count-by-day';
import { SalesTotalByDayChart } from './chart/sales-total-by-day';
import { SessionsChartWrapper } from './chart/sessions';
import { TicketsGeneralChart } from './chart/tickets-general';
import {
  ChartGroupCard,
  ChartGroupCardContent,
  ChartGroupCardHeader,
  ChartGroupCardTitle,
  IndicatorCard,
  toDeductionValue,
} from './components';
import { ExportFlowWrapper } from './export-flow-wrapper';
import {
  DashboardFilters,
  DashboardFiltersDatePicker,
  DashboardFiltersPaymentMethods,
  DashboardFiltersTickets,
  dashboardFiltersAtom,
} from './filters';

export interface DashboardProps {
  userEmail?: string;
}

export const Dashboard = ({ userEmail }: DashboardProps) => {
  const t = useTranslation();

  return (
    <div className='grid grid-cols-2 gap-xxs'>
      <header className='flex flex-row sm:flex-col justify-between gap-sm col-span-2'>
        <div className='space-y-nano'>
          <h1 className='ts-heading-sm sm:ts-heading-md text-ctx-content-title'>{t('dashboard-title')}</h1>
          <p className='ts-paragraph-xxs sm:ts-paragraph-xs text-ctx-content-base'>{t('dashboard-description')}</p>
        </div>
        <DashboardFilters>
          <DashboardFiltersDatePicker />
          <DashboardFiltersPaymentMethods />
          <DashboardFiltersTickets />
        </DashboardFilters>
      </header>

      <Suspense fallback={<DashboardSkeleton />}>
        <DashboardData />
      </Suspense>

      <ExportFlowWrapper userEmail={userEmail} />
    </div>
  );
};

type CategoryTab = 'tickets' | 'products';

const DashboardData = () => {
  const t = useTranslation();
  const { eventId = '' } = useParams();
  const dashboardFilters = useAtomValue(dashboardFiltersAtom);
  const { data } = useSuspenseQuery(eventDashboardQueries.metrics(eventId, dashboardFilters));
  const [activeTab, setActiveTab] = useState<CategoryTab>('tickets');

  return (
    <>
      <ChartGroupCard className='col-span-2'>
        <ChartGroupCardHeader>
          <ChartGroupCardTitle tooltip={t('tooltip.dashboard-financial-resume')}>
            <CurrencyDollar size={24} />
            {t('financial-resume')}
          </ChartGroupCardTitle>
        </ChartGroupCardHeader>
        <ChartGroupCardContent className='grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-mini'>
          <IndicatorCard label={t('gross-total')} value={data.summary.grossTotal} type='money' />
          <IndicatorCard label={t('coupon-discount-label')} value={-data.summary.couponDiscount} type='money' />
          <IndicatorCard
            label={t('fee-applied')}
            value={-Number(data.summary.platformFeePassed + data.summary.platformFeeAbsorbed)}
            type='money'
          />
          <IndicatorCard
            label={t('refunded-amount')}
            value={toDeductionValue(data.summary.refundedTotal ?? 0)}
            type='money'
          />
          <IndicatorCard
            label={t('net-total')}
            value={data.summary.netTotal}
            type='money'
            className='bg-base-primary-100/intense'
          />
        </ChartGroupCardContent>
      </ChartGroupCard>

      <ChartGroupCard className='col-span-2'>
        <ChartGroupCardHeader>
          <ChartGroupCardTitle tooltip={t('tooltip.dashboard-event-metrics')}>
            <ChartBar size={24} />
            {t('event-metrics')}
          </ChartGroupCardTitle>
        </ChartGroupCardHeader>
        <ChartGroupCardContent className='grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-mini'>
          <IndicatorCard label={t('completed-sales')} value={data.summary.completedSales} />
          {data.hasProducts && (
            <IndicatorCard label={t('dashboard.products-acquired')} value={data.summary.productsAcquired} />
          )}
          <IndicatorCard label={t('total-participants')} value={data.summary.totalParticipants} />
          <IndicatorCard label={t('peak-sales-date')} value={data.summary.peakSalesDate} type='date' />
          <IndicatorCard label={t('peak-sales-value')} value={data.summary.peakSalesValue} type='money' />
        </ChartGroupCardContent>
      </ChartGroupCard>

      <ChartGroupCard className='col-span-2'>
        {data.hasProducts && (
          <ChartGroupCardHeader>
            <ChartGroupCardTitle tooltip={t('tooltip.dashboard-details-by-category')}>
              <ChartBar size={24} />
              {t('dashboard.details-by-category')}
            </ChartGroupCardTitle>
          </ChartGroupCardHeader>
        )}
        {data.hasProducts && (
          <div className='flex gap-nano' role='tablist'>
            <button
              type='button'
              role='tab'
              aria-selected={activeTab === 'tickets'}
              onClick={() => setActiveTab('tickets')}
              className={cn(
                'flex items-center gap-quark px-mini py-nano rounded-pill ts-subtitle-xxs transition-colors',
                activeTab === 'tickets'
                  ? 'bg-ctx-content-button text-base-neutral-white'
                  : 'bg-base-gray-100 text-ctx-content-base hover:bg-base-gray-200',
              )}
            >
              <Ticket size={16} />
              {t('dashboard.tabs.tickets')}
            </button>
            <button
              type='button'
              role='tab'
              aria-selected={activeTab === 'products'}
              onClick={() => setActiveTab('products')}
              className={cn(
                'flex items-center gap-quark px-mini py-nano rounded-pill ts-subtitle-xxs transition-colors',
                activeTab === 'products'
                  ? 'bg-ctx-content-button text-base-neutral-white'
                  : 'bg-base-gray-100 text-ctx-content-base hover:bg-base-gray-200',
              )}
            >
              <Cube size={16} />
              {t('dashboard.tabs.products')}
            </button>
          </div>
        )}
        <ChartGroupCardContent className='grid grid-cols-1 lg:grid-cols-2 gap-mini' role='tabpanel'>
          {activeTab === 'tickets' || !data.hasProducts ? (
            <>
              <TicketsGeneralChart data={data.tickets} />
              <SalesByTypeChart data={data.tickets} />
              <SalesByStatusChart data={data.salesByStatus} />
              <CheckInChart data={data.checkin} />
              <SessionsChartWrapper />
              <SalesByTicketsChart data={data.tickets} />
            </>
          ) : (
            <Suspense fallback={<ProductChartsSkeleton />}>
              <ProductChartsContent eventId={eventId} />
            </Suspense>
          )}
        </ChartGroupCardContent>
      </ChartGroupCard>

      <ChartGroupCard className='col-span-2'>
        <ChartGroupCardHeader>
          <ChartGroupCardTitle tooltip={t('tooltip.dashboard-general-sales')}>
            <CurrencyDollar size={24} />
            {t('dashboard.general-sales')}
          </ChartGroupCardTitle>
        </ChartGroupCardHeader>
        <ChartGroupCardContent className='grid grid-cols-1 lg:grid-cols-2 gap-mini'>
          <SalesCountByDayChart data={data.dailySales} />
          <SalesTotalByDayChart data={data.dailySales} />
          <SalesCountAndTotalByDayChart data={data.dailySales} />
          <CouponsChart data={data.coupons} />
          <SalesByPaymentMethod data={data.paymentMethod} />
        </ChartGroupCardContent>
      </ChartGroupCard>
    </>
  );
};

const ProductChartsContent = ({ eventId }: { eventId: string }) => {
  const { data } = useSuspenseQuery(eventDashboardQueries.productMetrics(eventId));
  return (
    <>
      <ProductsAvailability data={data.availability} />
      <ProductsByType data={data.soldByType} />
      <ProductsWithdrawn data={data.withdrawal} />
      <ProductsTopRevenue data={data.topRevenue} />
      <ProductsStockLevel data={data.stockLevel} />
    </>
  );
};

const ProductChartsSkeleton = () => (
  <>
    <Skeleton className='min-h-[400px]' />
    <Skeleton className='min-h-[400px]' />
    <Skeleton className='min-h-[400px]' />
    <Skeleton className='min-h-[320px]' />
    <Skeleton className='min-h-[320px]' />
  </>
);

const DashboardSkeleton = () => (
  <>
    <div className='grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] col-span-2 gap-mini'>
      {Array.from({ length: 7 }).map((_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: Nesse caso podemos usar index como key pois a ordem desses itens nunca irá mudar
        <Skeleton key={index} className='min-h-[5.625rem] p-mini' />
      ))}
    </div>

    <div className='grid grid-cols-1 lg:grid-cols-2 col-span-2 gap-mini'>
      <Skeleton className='min-h-[400px]' />
      <Skeleton className='min-h-[400px]' />
    </div>
  </>
);
